<template>
    <div class="section pt-0 mt-0">
        <div class="listed-detail mt-1 page-title">
            <h3 class="text-center mal-text mt-1 custom-title">Faculty Listing</h3>
        </div>
    </div>
    <div class="section pt-0" v-if="isStateAdmin">
        <div class="faculty-tabs">
            <button
                type="button"
                class="faculty-tab"
                :class="{ active: activeTab === 'list' }"
                @click="activeTab = 'list'"
            >
                Faculty List
            </button>
            <button
                type="button"
                class="faculty-tab"
                :class="{ active: activeTab === 'report' }"
                @click="switchToReportTab"
            >
                Meet Report
            </button>
        </div>
    </div>
    <template v-if="activeTab === 'list'">
    <div id="search">
        <form class="search-form" v-if="showDistrictFilter">
            <label class="label text-primary" style="margin-bottom:0px;" for="filter_district"><span>District</span></label>
            <div class="form-group searchbox mal-text">
                <select class="form-control custom-select" v-model="filter_district" @change="getFaculties()">
                    <option value="">All</option>
                    <option v-for="district in district_list" :key="district.id" :value="district.id">
                        {{ district.name }}
                    </option>
                </select>
                <i class="input-icon">
                    <ion-icon name="filter-outline"></ion-icon>
                </i>
            </div>
        </form>
        <form class="search-form">
            <div class="form-group searchbox">
                <input type="text" class="form-control" placeholder="Search faculty name / mobile / centre"
                    v-model="search" @input="onSearchInput">
                <i class="input-icon">
                    <ion-icon name="search-outline"></ion-icon>
                </i>
            </div>
        </form>
    </div>
    <div class="section text-center mt-1" v-if="loading">
        <div class="spinner-border text-secondary" role="status"></div>
    </div>
    <div class="listed-detail mt-3" v-if="responseData.centers.total == 0 && !loading">
        <h3 class="text-center mt-2 text-primary">No faculties found</h3>
    </div>
    <div class="section pb-2" v-if="!loading">
        <div class="faculty-card" v-for="center in responseData.centers.data" :key="center.id">
            <div class="faculty-icon">
                <ion-icon name="person-outline"></ion-icon>
            </div>
            <div class="faculty-info">
                <div class="faculty-name">{{ center.faculty ? center.faculty.name : '-' }}</div>
                <div class="faculty-meta">{{ center.center_name }}</div>
                <div class="faculty-meta faculty-meta-sub">
                    {{ center.district ? center.district.name : '' }}
                    <span v-if="levelName(center)">· {{ levelName(center) }}</span>
                </div>
            </div>
            <a
                v-if="center.faculty && center.faculty.mobile"
                :href="'tel:' + center.faculty.mobile"
                class="faculty-call"
            >
                <ion-icon name="call-outline"></ion-icon>
            </a>
        </div>
    </div>
    <div class="section mt-2" v-if="!loading">
        <vue-awesome-paginate
            v-if="responseData.centers.total > 0"
            :total-items="responseData.centers.total"
            :items-per-page="responseData.centers.per_page"
            :max-pages-shown="3"
            v-model="responseData.centers.current_page"
            :on-click="getFaculties"
            :show-ending-buttons="true"
            :show-breakpoint-buttons="false"
        />
    </div>
    </template>

    <template v-if="activeTab === 'report' && isStateAdmin">
        <div class="section text-center mt-1" v-if="reportLoading">
            <div class="spinner-border text-secondary" role="status"></div>
        </div>
        <div class="listed-detail mt-3" v-if="!reportLoading && meetings.length === 0">
            <h3 class="text-center mt-2 text-primary">No faculty meets found</h3>
        </div>
        <div class="section pb-2" v-if="!reportLoading">
            <div class="meet-report-card" v-for="meeting in meetings" :key="meeting.id">
                <div class="meet-report-header" @click="toggleMeetingDetail(meeting)">
                    <div>
                        <div class="meet-report-name">{{ meeting.name }}</div>
                        <div class="meet-report-time">{{ formatTime(meeting.meeting_time) }}</div>
                    </div>
                    <ion-icon
                        :name="expandedMeetingId === meeting.id ? 'chevron-up-outline' : 'chevron-down-outline'"
                    ></ion-icon>
                </div>
                <div class="meet-report-counts">
                    <button
                        type="button"
                        class="count-badge count-present"
                        :class="{ 'count-badge-active': expandedMeetingId === meeting.id && attendanceFilter === 'P' }"
                        @click.stop="filterByStatus(meeting, 'P')"
                    >Present: {{ meeting.present_count }}</button>
                    <button
                        type="button"
                        class="count-badge count-leave"
                        :class="{ 'count-badge-active': expandedMeetingId === meeting.id && attendanceFilter === 'L' }"
                        @click.stop="filterByStatus(meeting, 'L')"
                    >Leave: {{ meeting.leave_count }}</button>
                    <button
                        type="button"
                        class="count-badge count-pending"
                        :class="{ 'count-badge-active': expandedMeetingId === meeting.id && attendanceFilter === 'N' }"
                        @click.stop="filterByStatus(meeting, 'N')"
                    >Pending: {{ meeting.pending_count }}</button>
                </div>
                <div class="meet-report-detail" v-if="expandedMeetingId === meeting.id">
                    <div class="section text-center mt-1" v-if="detailLoading">
                        <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
                    </div>
                    <div class="faculty-response-row" v-for="r in filteredMeetingResponses" :key="r.id">
                        <div class="faculty-response-info">
                            <div class="faculty-response-name">{{ r.name }}</div>
                            <div class="faculty-response-meta">{{ r.center_name }} · {{ r.district }}</div>
                            <div class="faculty-response-meta" v-if="r.attendance === 'L' && r.remarks">{{ r.remarks }}</div>
                        </div>
                        <span class="meet-status" :class="statusClass(r.attendance)">{{ statusLabel(r.attendance) }}</span>
                    </div>
                    <div class="text-muted text-center mt-1" v-if="!detailLoading && filteredMeetingResponses.length === 0">
                        No faculty match this filter.
                    </div>
                </div>
            </div>
        </div>
    </template>

    <alert-model :type="alert_type" :message="alert_message" ref="alert_model"></alert-model>
</template>

<script>
import UserService from "../../services/user.service";
import AlertModel from "../layouts/AlertModel.vue";

export default {
    name: "QHLSFacultyListing",
    components: {
        AlertModel,
    },
    data() {
        return {
            loading: false,
            alert_type: "",
            alert_message: "",
            user_level: "",
            user_level_id: "",
            filter_district: "",
            search: "",
            searchTimer: null,
            district_list: [],
            responseData: {
                centers: {
                    data: [],
                    total: 0,
                    per_page: 20,
                    current_page: 1,
                },
            },
            activeTab: "list",
            reportLoading: false,
            detailLoading: false,
            meetings: [],
            expandedMeetingId: null,
            meetingResponses: [],
            attendanceFilter: null,
        };
    },
    computed: {
        currentUser() {
            return this.$store.state.auth.user;
        },
        showDistrictFilter() {
            return parseInt(String(this.currentUser?.user_level ?? ''), 10) === 1;
        },
        isStateAdmin() {
            return parseInt(String(this.currentUser?.user_level ?? ''), 10) === 1;
        },
        filteredMeetingResponses() {
            if (!this.attendanceFilter) return this.meetingResponses;
            return this.meetingResponses.filter((r) => r.attendance === this.attendanceFilter);
        },
    },
    mounted() {
        if (!this.currentUser) {
            location.replace("/");
            return;
        }
        const userLevel = parseInt(String(this.currentUser.user_level), 10);
        if (!(userLevel >= 1 && userLevel <= 4)) {
            this.alert_type = "Warning";
            this.alert_message = "You have no permission!";
            this.$refs.alert_model.openModel();
            return;
        }
        this.user_level = parseInt(this.$route.params.sub_level || this.currentUser.user_level, 10);
        this.user_level_id = parseInt(this.$route.params.id || 0, 10);
        if (userLevel === 2) {
            this.filter_district = this.currentUser.district_id || "";
        }
        this.$root.updateParent("Faculty Listing");
        if (this.showDistrictFilter) {
            this.getDistrictList();
        }
        this.getFaculties();
    },
    methods: {
        levelName(center) {
            const name = center.unit && center.unit.name ? center.unit.name : (center.zone ? center.zone.name : '');
            if (!name) return '';
            return name + ' ' + (center.user_level == 3 ? 'Zone' : 'Unit');
        },
        onSearchInput() {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => {
                this.getFaculties();
            }, 400);
        },
        getDistrictList() {
            UserService.authPostRequest("get-district-by-id", {}).then(
                (response) => {
                    if (response.data.status === "success") {
                        this.district_list = response.data.data.list;
                    }
                },
                () => {
                    this.district_list = [];
                }
            );
        },
        getFaculties(page = 1) {
            this.loading = true;
            const data = {
                user_level_id: this.user_level_id,
                user_level: this.user_level,
                district_id: this.filter_district,
                search: this.search,
            };
            UserService.authPostRequest(`get-qhls-faculty-list?page=${page}`, data).then(
                (response) => {
                    this.loading = false;
                    if (response.data.status === "success") {
                        this.responseData = response.data.data;
                    } else {
                        this.alert_type = "Warning";
                        this.alert_message = response.data.message;
                        this.$refs.alert_model.openModel();
                    }
                },
                (error) => {
                    this.loading = false;
                    this.alert_type = "Error";
                    this.alert_message =
                        (error.response && error.response.data && error.response.data.message) ||
                        error.message ||
                        error.toString();
                    this.$refs.alert_model.openModel();
                }
            );
        },
        statusLabel(status) {
            if (status === "P") return "Present";
            if (status === "L") return "Leave";
            return "Not Responded";
        },
        statusClass(status) {
            if (status === "P") return "status-present";
            if (status === "L") return "status-leave";
            return "status-pending";
        },
        formatTime(iso) {
            if (!iso) return "";
            const d = new Date(iso);
            return d.toLocaleString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        },
        switchToReportTab() {
            this.activeTab = "report";
            if (this.meetings.length === 0) {
                this.getMeetReport();
            }
        },
        getMeetReport() {
            this.reportLoading = true;
            UserService.authPostRequest("get-qhls-faculty-meet-report", {}).then(
                (response) => {
                    this.reportLoading = false;
                    if (response.data.status === "success") {
                        this.meetings = response.data.data.meetings.data || [];
                    } else {
                        this.alert_type = "Warning";
                        this.alert_message = response.data.message;
                        this.$refs.alert_model.openModel();
                    }
                },
                (error) => {
                    this.reportLoading = false;
                    this.alert_type = "Error";
                    this.alert_message =
                        (error.response && error.response.data && error.response.data.message) ||
                        error.message ||
                        error.toString();
                    this.$refs.alert_model.openModel();
                }
            );
        },
        toggleMeetingDetail(meeting) {
            if (this.expandedMeetingId === meeting.id) {
                this.expandedMeetingId = null;
                this.meetingResponses = [];
                this.attendanceFilter = null;
                return;
            }
            this.expandMeeting(meeting);
        },
        expandMeeting(meeting) {
            this.expandedMeetingId = meeting.id;
            this.attendanceFilter = null;
            this.detailLoading = true;
            this.meetingResponses = [];
            UserService.authPostRequest("get-qhls-faculty-meet-report-detail", { meeting_id: meeting.id }).then(
                (response) => {
                    this.detailLoading = false;
                    if (response.data.status === "success") {
                        this.meetingResponses = response.data.data.responses || [];
                    }
                },
                () => {
                    this.detailLoading = false;
                }
            );
        },
        filterByStatus(meeting, status) {
            if (this.expandedMeetingId !== meeting.id) {
                this.expandMeeting(meeting);
                this.attendanceFilter = status;
                return;
            }
            this.attendanceFilter = this.attendanceFilter === status ? null : status;
        },
    },
};
</script>

<style scoped>
.faculty-card {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #fff;
    border-radius: 10px;
    padding: 10px 12px;
    margin-top: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.faculty-icon {
    width: 38px;
    height: 38px;
    flex-shrink: 0;
    border-radius: 50%;
    background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
}

.faculty-info {
    flex: 1;
    min-width: 0;
}

.faculty-name {
    font-weight: 600;
    font-size: 14px;
    color: #1f2937;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.faculty-meta {
    font-size: 12px;
    color: #6b7280;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.faculty-meta-sub {
    color: #9ca3af;
}

.faculty-call {
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: #f3f4ff;
    color: #6d28d9;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 17px;
}

.faculty-tabs {
    display: flex;
    gap: 8px;
    background: #f3f4f6;
    border-radius: 10px;
    padding: 4px;
}

.faculty-tab {
    flex: 1;
    border: none;
    background: transparent;
    padding: 8px 0;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
}

.faculty-tab.active {
    background: #6d28d9;
    color: #fff;
}

.meet-report-card {
    background: #fff;
    border-radius: 10px;
    padding: 12px 14px;
    margin-top: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.meet-report-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
}

.meet-report-name {
    font-weight: 600;
    font-size: 14px;
    color: #1f2937;
}

.meet-report-time {
    font-size: 12px;
    color: #6b7280;
    margin-top: 2px;
}

.meet-report-counts {
    display: flex;
    gap: 6px;
    margin-top: 8px;
    flex-wrap: wrap;
}

.count-badge {
    font-size: 11px;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 12px;
    border: 1.5px solid transparent;
    cursor: pointer;
    line-height: 1.4;
}

.count-present {
    background: #dcfce7;
    color: #15803d;
}

.count-leave {
    background: #fee2e2;
    color: #b91c1c;
}

.count-pending {
    background: #fef3c7;
    color: #b45309;
}

.count-badge-active {
    border-color: currentColor;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.06);
}

.meet-report-detail {
    border-top: 1px solid #f1f3f7;
    margin-top: 10px;
    padding-top: 8px;
}

.faculty-response-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 6px 0;
    border-bottom: 1px solid #f7f7f9;
}

.faculty-response-row:last-child {
    border-bottom: 0;
}

.faculty-response-name {
    font-weight: 600;
    font-size: 13px;
    color: #1f2937;
}

.faculty-response-meta {
    font-size: 11px;
    color: #9ca3af;
}

.meet-status {
    font-size: 11px;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 12px;
    white-space: nowrap;
    flex-shrink: 0;
}

.status-present {
    background: #dcfce7;
    color: #15803d;
}

.status-leave {
    background: #fee2e2;
    color: #b91c1c;
}

.status-pending {
    background: #fef3c7;
    color: #b45309;
}
</style>
